import React, { useState, useEffect, useCallback } from "react";
import { History } from "lucide-react";
import { fetchWithAuth } from "../../utils/api";
import EmptyState from "../common/EmptyState";
import { chainDisplayLabel } from "../../utils/assetLogos";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const fmtDateTime = (v) =>
  v
    ? new Date(v).toLocaleString(undefined, {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

/**
 * The merchant's auto-conversion settings over time.
 *
 * This exists to answer "why is this balance in this asset?". The current
 * settings cannot answer it: by the time anyone asks, the target may have been
 * changed several times, and each past payment was converted under whichever
 * version was in force at the time. Those versions are immutable — a closed
 * version is never edited, only superseded.
 */
const ConfigHistoryTimeline = ({ isTestnet = false }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await fetchWithAuth(
        `${API_BASE_URL}/merchant/auto-conversion/history?is_testnet=${isTestnet ? "true" : "false"}`
      );
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.message || "Failed to load history");
      setVersions(json.data || []);
    } catch (err) {
      console.error("[AutoConversion] config history load failed:", err.message);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [isTestnet]);

  useEffect(() => {
    load();
  }, [load]);

  const describe = (v) => {
    if (!v.enabled) return "Disabled";
    const chain = v.target_chain ? chainDisplayLabel(v.target_chain) || v.target_chain : null;
    // same_chain settles on whichever chain the payment arrived on, so there is
    // no single target chain to name.
    const chainLabel = v.settlement_mode === "same_chain" ? "Same chain" : chain || "—";
    return `${chainLabel} / ${v.target_asset}`;
  };

  return (
    <div className="ach-card">
      <div className="ach-head">
        <h2>Configuration History</h2>
        <p>View your auto-conversion settings over time.</p>
      </div>

      {loading ? (
        <div className="ach-loading">
          {[0, 1, 2].map((i) => (
            <div key={i} className="ach-skeleton" />
          ))}
        </div>
      ) : error ? (
        <div className="ach-error">
          <p>Couldn&apos;t load configuration history.</p>
          <button type="button" className="ach-retry" onClick={load}>
            Try again
          </button>
        </div>
      ) : versions.length === 0 ? (
        <EmptyState
          icon={<History size={24} />}
          title="No history yet"
          subtitle="Your settings history starts the first time you save a configuration."
        />
      ) : (
        <ol className="ach-timeline">
          {versions.map((v) => (
            <li key={v.version} className={`ach-item${v.is_current ? " ach-item-current" : ""}`}>
              <span className="ach-dot" aria-hidden="true" />
              <div className="ach-body">
                <div className="ach-row">
                  <span className={`ach-tag${v.is_current ? " ach-tag-current" : ""}`}>
                    {v.is_current ? "Current" : "Previous"}
                  </span>
                  <span className="ach-version">v{v.version}</span>
                </div>
                <div className="ach-target">{describe(v)}</div>
                <div className="ach-range">
                  {fmtDateTime(v.effective_from)} &ndash;{" "}
                  {v.effective_to ? fmtDateTime(v.effective_to) : "Present"}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}

      <style jsx>{styles}</style>
    </div>
  );
};

const styles = `
  .ach-card {
    background: var(--bg-surface);
    border: 1px solid var(--line);
    border-radius: var(--r-lg);
    box-shadow: var(--sh-sm);
    padding: 22px 24px;
    height: 450px;
    overflow-y: scroll;
  }
  .ach-head h2 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: var(--ink);
  }
  .ach-head p {
    margin: 3px 0 18px;
    font-size: 13px;
    color: var(--ink-secondary);
  }

  .ach-timeline {
    list-style: none;
    margin: 0;
    padding: 0;
    position: relative;
  }
  /* One continuous rail behind the dots, so the versions read as a sequence
     rather than three unrelated cards. */
  .ach-timeline::before {
    content: "";
    position: absolute;
    left: 5px;
    top: 6px;
    bottom: 6px;
    width: 2px;
    background: var(--line);
  }
  .ach-item {
    position: relative;
    padding: 0 0 22px 26px;
  }
  .ach-item:last-child { padding-bottom: 0; }
  .ach-dot {
    position: absolute;
    left: 0;
    top: 5px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: var(--bg-surface);
    border: 2px solid var(--line-strong);
  }
  .ach-item-current .ach-dot {
    background: var(--brand-600);
    border-color: var(--brand-600);
  }
  .ach-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .ach-tag {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 3px 9px;
    font-size: 11px;
    font-weight: 600;
    background: var(--bg-inset);
    color: var(--ink-secondary);
  }
  .ach-tag-current {
    background: rgba(109, 93, 246, 0.12);
    color: var(--brand-700);
  }
  .ach-version {
    margin-left: auto;
    font-size: 11px;
    font-weight: 700;
    color: var(--ink-muted);
    background: var(--bg-subtle);
    border: 1px solid var(--line);
    border-radius: var(--r-sm);
    padding: 2px 7px;
  }
  .ach-target {
    margin-top: 6px;
    font-size: 14px;
    font-weight: 600;
    color: var(--ink);
  }
  .ach-range {
    margin-top: 3px;
    font-size: 12px;
    color: var(--ink-muted);
  }

  .ach-loading { display: flex; flex-direction: column; gap: 14px; }
  .ach-skeleton {
    height: 52px;
    border-radius: var(--r-sm);
    background: linear-gradient(90deg, var(--bg-inset) 25%, var(--bg-subtle) 50%, var(--bg-inset) 75%);
    background-size: 200% 100%;
    animation: ach-shimmer 1.4s ease infinite;
  }
  @keyframes ach-shimmer {
    from { background-position: 200% 0; }
    to { background-position: -200% 0; }
  }

  .ach-error {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    font-size: 13px;
    color: var(--ink-secondary);
  }
  .ach-error p { margin: 0; }
  .ach-retry {
    background: var(--bg-subtle);
    border: 1px solid var(--line);
    border-radius: var(--r-sm);
    color: var(--ink-secondary);
    font-size: 12px;
    font-weight: 600;
    padding: 7px 11px;
    cursor: pointer;
  }
`;

export default ConfigHistoryTimeline;
